import type { Occurrence, Payment } from '@klopotun/contracts';
import { addDays, differenceInDays, formatCivilDate, parseCivilDate } from '@klopotun/domain';
import { useOccurrencesQuery } from '../../../entities/occurrence/index.js';
import { usePaymentsQuery } from '../../../entities/payment/index.js';
import { formatExpectedAmount } from '../../../features/occurrence-view/index.js';
import { formatVariableAmount } from '../../../features/payment-view/index.js';
import { EmptyState, ErrorState, List, ListItem, Spinner } from '../../../shared/ui/index.js';
import { formatDayAndMonth, formatDaysAhead } from '../lib/day-text.js';
import styles from './calm-state.module.css';

const COMING_DAYS = 7;

const whenOf = (occurrence: Occurrence, today: string): string => {
  const daysAhead = differenceInDays(parseCivilDate(today), parseCivilDate(occurrence.dueDate));

  return `${formatDaysAhead(daysAhead)} · ${formatDayAndMonth(occurrence.dueDate)}`;
};

const amountOf = (occurrence: Occurrence, payment: Payment | undefined): string =>
  formatExpectedAmount(
    occurrence,
    payment === undefined ? undefined : formatVariableAmount(payment),
  );

type ComingChargesProps = {
  occurrences: Occurrence[] | undefined;
  payments: Payment[] | undefined;
  failureMessage: string | null;
  today: string;
  onRetry: () => void;
  onOpen: (paymentId: string) => void;
};

const ComingCharges = ({
  occurrences,
  payments,
  failureMessage,
  today,
  onRetry,
  onOpen,
}: ComingChargesProps) => {
  if (failureMessage !== null) {
    return (
      <ErrorState
        title="Не вдалося завантажити найближчі платежі"
        message={failureMessage}
        onRetry={onRetry}
      />
    );
  }

  if (occurrences === undefined || payments === undefined) {
    return (
      <div className={styles.centered}>
        <Spinner label="Завантаження" />
      </div>
    );
  }

  const paymentById = new Map(payments.map((payment) => [payment.id, payment] as const));
  const hasNoCharges = occurrences.length === 0;

  return (
    <List header="Найближчі 7 днів" footer={hasNoCharges ? 'Платежів не заплановано.' : undefined}>
      {hasNoCharges
        ? undefined
        : occurrences.map((occurrence) => {
            const payment = paymentById.get(occurrence.paymentId);

            return (
              <ListItem
                key={occurrence.id}
                title={payment?.name ?? 'Підписка'}
                subtitle={whenOf(occurrence, today)}
                value={amountOf(occurrence, payment)}
                chevron
                onClick={() => {
                  onOpen(occurrence.paymentId);
                }}
              />
            );
          })}
    </List>
  );
};

type CalmStateProps = {
  today: string;
  onOpen: (paymentId: string) => void;
};

export const CalmState = ({ today, onOpen }: CalmStateProps) => {
  const lastDay = formatCivilDate(addDays(parseCivilDate(today), COMING_DAYS));
  const occurrences = useOccurrencesQuery({ status: 'planned', from: today, to: lastDay });
  const payments = usePaymentsQuery();
  const failure = occurrences.error ?? payments.error;

  return (
    <div className={styles.calm}>
      <EmptyState title="Усе спокійно" description="Зараз нічого не потребує вашої уваги." />
      <ComingCharges
        occurrences={occurrences.data}
        payments={payments.data}
        failureMessage={failure === null ? null : failure.message}
        today={today}
        onRetry={() => {
          void occurrences.refetch();
          void payments.refetch();
        }}
        onOpen={onOpen}
      />
    </div>
  );
};
